import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** What a location without a timezone of its own is assumed to be in, as on the server. */
const DEFAULT_TIMEZONE = 'Europe/Zurich';

const TIMESTAMP_FORMAT = 'DD MMM YYYY HH:mm:ss';

/**
 * A session's timestamp, in the clock of the location it happened at.
 *
 * The server sends these as epoch milliseconds - an instant, with no zone attached - so `dayjs(ms)`
 * renders them in whatever zone the browser happens to be in. That reads correctly only while the
 * person sits in the same country as the charger: the same session viewed from Zurich and from Kyiv
 * showed two different times, and from a machine set to UTC it showed neither. A charging session
 * belongs to its location, and the hour and day-of-week stored beside every cost item are local for
 * exactly that reason - so these are rendered local too, and the two finally agree.
 */
export const formatLocationTime = (
    value: number | string | null | undefined,
    locationTimezone?: string | null
): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const instant = dayjs(value);
    if (!instant.isValid()) {
        return '-';
    }

    return inZone(instant, locationTimezone) ?? inZone(instant, DEFAULT_TIMEZONE) ?? '-';
};

/**
 * `.tz()` throws a `RangeError` on a zone name the browser does not know - it does not hand back an
 * invalid date - so a single mistyped `location.timezone` would take the whole page down rather than
 * misprint one row. Hence the catch, and hence the fall back to the default before giving up.
 */
const inZone = (instant: dayjs.Dayjs, zone?: string | null): string | null => {
    if (!zone) {
        return null;
    }
    try {
        return instant.tz(zone).format(TIMESTAMP_FORMAT);
    } catch {
        return null;
    }
};
